import type { ComponentProps } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  `inline-flex w-fit flex-row items-center gap-1 font-medium px-1.5 py-0.5 rounded-2xl`,
  {
    variants: {
      variant: {
        purple: "bg-purple border border-purple-border text-purple-foreground",
        blue: "bg-blue border border-blue-border text-blue-foreground",
        teal: "bg-teal border border-teal-border text-teal-foreground",
        pink: "bg-pink-light border border-pink-border text-pink-foreground",
        orange: "bg-orange-light border border-orange-border text-orange-foreground",
        gray: "bg-gray-light border border-gray-border text-gray-foreground",
        red: "bg-red-light border border-red-border text-red-foreground",
      },
      size: {
        default: "px-1.5 py-0.5 text-base",
        sm: "px-2 py-0.5 text-xs",
        icon: "p-0.5 rounded-md border-none",
      },
    },
    defaultVariants: {
      variant: "purple",
      size: "default",
    },
  },
);

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

// Status → variant mappings, grouped by domain so each domain's logic lives
// together. Add a domain its own map and spread it into STATUS_BADGE_VARIANTS.
const REVENUE_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  processing: "blue",
  draft: "gray",
  settled: "teal",
};

const CONTRACT_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  draft: "gray",
  active: "teal",
  ended: "red",
};

const RELEASE_TYPE_VARIANTS: Record<string, BadgeVariant> = {
  album: "blue",
  ep: "teal",
  single: "purple",
  compilation: "pink",
  soundtrack: "orange",
  other: "gray",
};

const STATUS_BADGE_VARIANTS: Record<string, BadgeVariant> = {
  ...REVENUE_STATUS_VARIANTS,
  ...CONTRACT_STATUS_VARIANTS,
  ...RELEASE_TYPE_VARIANTS,
};

const getStatusBadgeVariant = (status?: string): BadgeVariant =>
  STATUS_BADGE_VARIANTS[status?.toLowerCase() ?? ""] ?? "gray";

function Badge({
  className,
  variant,
  asChild = false,
  size,
  ...props
}: ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean; size?: "default" | "sm" | "icon" }) {
  const Comp = asChild ? Slot : "span";
  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants, getStatusBadgeVariant };
